/**
 * Browser-safe Supabase client factory. Uses anon key only — never service role.
 * Both apps/web (Vite) and apps/marketing (Next.js client components) consume this.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export interface BrowserClientOptions {
  url: string;
  anonKey: string;
  /** Persist the session in localStorage. Default true. */
  persistSession?: boolean;
  /** Auto-refresh tokens. Default true. */
  autoRefreshToken?: boolean;
  /** Custom storage key for the session. Default 'insolech-auth'. */
  storageKey?: string;
}

/**
 * No-op replacement for the default `navigatorLock` used by @supabase/auth-js
 * for cross-tab coordination. The default uses `navigator.locks.request(...)`,
 * which can hang indefinitely when:
 *   - A browser extension (MetaMask SES lockdown, Polkadot.js, Brave Wallet,
 *     etc.) tampers with the Locks API or window.crypto.
 *   - A page's service-worker registration is in a broken/reconnect loop.
 *
 * Symptom: `getSession()` and `signInWithPassword()` never resolve. We saw
 * this in the wild on staging (Rafael's Chrome with crypto-wallet extensions).
 *
 * Trade-off: with this no-op, two open tabs can race to refresh the same
 * token, which is harmless (one of the requests just returns a still-valid
 * token). Cross-tab session sync still works via the storage event listener.
 */
const noopLock = async <R>(_name: string, _timeout: number, fn: () => Promise<R>): Promise<R> =>
  fn();

export function createBrowserClient(opts: BrowserClientOptions): SupabaseClient<Database> {
  return createClient<Database>(opts.url, opts.anonKey, {
    auth: {
      persistSession: opts.persistSession ?? true,
      autoRefreshToken: opts.autoRefreshToken ?? true,
      storageKey: opts.storageKey ?? 'insolech-auth',
      detectSessionInUrl: true,
      flowType: 'pkce',
      lock: noopLock,
    },
  });
}

export type { SupabaseClient };
